import { Link } from "react-router-dom";
import { useAuctionItems } from "@/hooks/useAuctionItems";
import { motion } from "motion/react";

const sectionVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.1 },
  },
};

const fadeUpVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] as const },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] as const },
  },
};

export default function AuctionSection() {
  const { items: auctionItems } = useAuctionItems();
  const displayItems = auctionItems.slice(0, 4);

  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-80px" }}
      variants={sectionVariants}
      className="w-full py-16 md:py-20 px-4 md:px-6 bg-[#FAFAF8]"
    >
      <div className="max-w-[1280px] mx-auto">
        {/* Section Header */}
        <motion.div
          variants={fadeUpVariants}
          className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-10 gap-4"
        >
          <div>
            <span className="inline-block px-3 py-1 text-xs font-medium bg-[#C4956A]/10 text-[#C4956A] rounded-full mb-3">
              전문가 PICK
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-[#1C1917]">
              이번 주 추천 경매 물건
            </h2>
            <p className="text-sm text-[#8C8780] mt-2">
              강스터디 전문가가 권리분석부터 수익성까지 꼼꼼히 따져 엄선했습니다
            </p>
          </div>
          <Link
            to="/auctions"
            className="flex items-center gap-1.5 text-sm font-medium text-[#C4956A] hover:text-[#B08050] transition-colors whitespace-nowrap"
          >
            전체 물건 보기
            <i className="ri-arrow-right-line"></i>
          </Link>
        </motion.div>

        {/* Auction Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-product-shop>
          {displayItems.map((item) => (
            <motion.div
              key={item.id}
              variants={cardVariants}
              whileHover={{ y: -6 }}
              transition={{ type: "spring", stiffness: 300, damping: 15 }}
            >
              <Link
                to={`/auctions/${item.id}`}
                className="group bg-white rounded-xl border border-[#E8E5E0] overflow-hidden hover:border-[#C4956A]/30 transition-all block"
              >
                {/* Image */}
                <div className="relative h-52 overflow-hidden">
                  {item.imageUrl ? (
                    <img loading="lazy" decoding="async"
                      src={item.imageUrl}
                      alt={`${item.propertyType} - ${item.location}`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#F5F2EE] to-[#E8E5E0] flex flex-col items-center justify-center gap-2">
                      <i className="ri-building-2-line text-4xl text-[#C4B8A8]"></i>
                      <span className="text-xs text-[#A89C8C]">{item.propertyType}</span>
                    </div>
                  )}
                  <div className="absolute top-3 left-3">
                    <span
                      className={`px-2.5 py-1 text-xs font-semibold rounded-full ${
                        item.grade === "A"
                          ? "bg-emerald-500 text-white"
                          : item.grade === "B"
                          ? "bg-amber-500 text-white"
                          : "bg-gray-400 text-white"
                      }`}
                    >
                      {item.grade}등급
                    </span>
                  </div>
                  <div className="absolute top-3 right-3">
                    <span className="px-2.5 py-1 text-xs font-medium bg-white/90 rounded-full text-[#1C1917]">
                      유찰 {item.failureCount}회
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-medium text-[#C4956A] bg-[#C4956A]/5 px-2 py-0.5 rounded">
                      {item.propertyType}
                    </span>
                    <span className="text-xs text-[#8C8780]">{item.court}</span>
                  </div>
                  <h3 className="text-sm font-semibold text-[#1C1917] mb-3 line-clamp-1">
                    {item.location}
                  </h3>

                  <div className="space-y-1.5">
                    <div className="flex justify-between text-xs">
                      <span className="text-[#8C8780]">감정가</span>
                      <span className="text-[#5C5650] font-medium">
                        {item.appraisedValue.toLocaleString()}원
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-[#8C8780]">최저가</span>
                      <span className="text-[#C4956A] font-bold">
                        {item.minimumPrice.toLocaleString()}원
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-[#8C8780]">입찰일</span>
                      <span className="text-[#5C5650] font-medium">
                        {item.auctionDate}
                      </span>
                    </div>
                  </div>

                  <div className="mt-3 pt-3 border-t border-[#F0EDE8] flex items-center justify-between">
                    <div className="flex items-center gap-1 text-xs text-[#8C8780]">
                      <i className="ri-user-line"></i>
                      <span>{item.hasTenant ? "임차인 있음" : "임차인 없음"}</span>
                    </div>
                    <span className="text-xs font-semibold text-emerald-600">
                      {item.expectedProfit != null
                        ? `예상수익 ${item.expectedProfit.toLocaleString()}원`
                        : "시세 분석 준비중"}
                    </span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>
  );
}